import clsx from 'clsx';

import {
  roundADOTQuantity,
  toADOTQuantity,
} from '@atom/components/common/requests/adotQuantityUtilities';
import {
  divideADOTConversion,
  formatADOTQuantityWithUnit,
  formatADOTUnitCost,
  isADOTBaseUnit,
  multiplyADOTConversion,
} from '@atom/components/common/requests/adotReplenishmentUtilities';
import { getReplenishmentSubtextOptions } from '@atom/components/common/requests/customTenantUtilities';
import { Button, DatePicker, Icon, TextField } from '@atom/mui';
import { WarningIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import {
  AssetRequestPurchaseUnit,
  ReplenishmentCreateInput,
} from '@atom/types/assetRequest';
import { convertDateToMillisGMTMidday } from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './adotView.module.css';

const styles = {
  label: {
    color: colors.neutral.dim,
  },
  icon: {
    marginRight: '1rem',
  },
  cancelButton: {
    marginRight: '0.5rem',
  },
};

type CostProperty = 'unitCost' | 'totalCost';

type ADOTReplenishmentFormProps = {
  title: string;
  purchaseUnit: AssetRequestPurchaseUnit;
  totalOrderedBase: number;
  totalReceivedBase?: number;
  inputs: Partial<ReplenishmentCreateInput>;
  updateInput: (value: any, property: keyof ReplenishmentCreateInput) => void;
  alwaysShowCalculate?: boolean;
  quantityError?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  confirmDisabled?: boolean;
  dataCyPrefix: string;
};

const ADOTReplenishmentForm = ({
  title,
  purchaseUnit,
  totalOrderedBase,
  totalReceivedBase = 0,
  inputs,
  updateInput,
  alwaysShowCalculate = false,
  quantityError = false,
  onConfirm,
  onCancel,
  confirmText = 'Create',
  confirmDisabled = false,
  dataCyPrefix,
}: ADOTReplenishmentFormProps) => {
  const handleNumberChange = (
    value: string,
    property: keyof ReplenishmentCreateInput,
  ) => updateInput(toADOTQuantity(value), property);

  const handleDateChange = (value: Date) =>
    updateInput(
      !value || value.toString() === 'Invalid Date'
        ? null
        : convertDateToMillisGMTMidday(value),
      'dateReceived',
    );

  const dateReceived = inputs?.dateReceived
    ? new Date(inputs.dateReceived)
    : null;

  const calculateValue = (property: CostProperty) => {
    const { quantityReceived, unitCost, totalCost } = inputs;

    const totalCostCalc = roundADOTQuantity(quantityReceived * unitCost);
    const unitCostCalc =
      quantityReceived === 0
        ? 0
        : roundADOTQuantity(totalCost / quantityReceived);

    updateInput(
      property === 'totalCost' ? totalCostCalc : unitCostCalc,
      property,
    );
  };

  const showBase = !isADOTBaseUnit(purchaseUnit);
  const hasQuantity = !isNilOrEmpty(inputs?.quantityReceived);
  const isEmptyOrZero = (value: number): boolean =>
    isNilOrEmpty(value) || value === 0;

  const showUnitCostCalculate =
    hasQuantity &&
    !isNilOrEmpty(inputs?.totalCost) &&
    (alwaysShowCalculate || isEmptyOrZero(inputs?.unitCost));

  const showTotalCostCalculate =
    hasQuantity &&
    !isNilOrEmpty(inputs?.unitCost) &&
    (alwaysShowCalculate || isEmptyOrZero(inputs?.totalCost));

  const baseQuantityText =
    showBase && hasQuantity
      ? formatADOTQuantityWithUnit(
          multiplyADOTConversion(inputs?.quantityReceived, purchaseUnit),
          purchaseUnit.baseUnit,
        )
      : null;

  const baseUnitCostText =
    showBase && !isNilOrEmpty(inputs?.unitCost)
      ? formatADOTUnitCost(
          divideADOTConversion(inputs?.unitCost, purchaseUnit),
          purchaseUnit.baseUnit,
        )
      : null;

  const totalRemaining = roundADOTQuantity(
    divideADOTConversion(totalOrderedBase - totalReceivedBase, purchaseUnit) -
      inputs?.quantityReceived,
  );

  const subtextValue =
    hasQuantity && !isNilOrEmpty(totalOrderedBase)
      ? getReplenishmentSubtextOptions(totalRemaining, purchaseUnit.unit)
      : null;

  const isWarning = subtextValue?.icon === WarningIcon;
  const iconColor = isWarning ? colors.brand.orange : colors.brand.green;

  const renderCalculate = (property: CostProperty) => (
    <div
      className={classes.calculateLink}
      onClick={() => calculateValue(property)}
      data-cy={`${dataCyPrefix}${
        property === 'unitCost' ? 'UnitCost' : 'TotalCost'
      }Calculate`}
    >
      Calculate
    </div>
  );

  return (
    <div className={classes.formContainer} data-cy={`${dataCyPrefix}Form`}>
      <div className={classes.formTitle}>{title}</div>
      <div className={classes.formSubtext}>
        The unit cost of this inventory item will be re-calculated by taking
        into account the quantity received and the unit cost of this
        replenishment.
      </div>
      <div className={classes.formInputRow}>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          value={inputs?.invoiceNumber || ''}
          label="Invoice Number"
          onChange={event => updateInput(event.target.value, 'invoiceNumber')}
          data-cy={`${dataCyPrefix}InvoiceNumber`}
        />
        <DatePicker
          inputLabelSx={styles.label}
          label="Date Received"
          value={dateReceived}
          onChange={handleDateChange}
          data-cy={`${dataCyPrefix}DateReceived`}
        />
        <TextField
          slotProps={{
            inputLabel: { style: styles.label },
            input: {
              endAdornment: (
                <span className={classes.inputAdornment}>
                  {purchaseUnit.unit}
                </span>
              ),
            },
          }}
          type="number"
          value={inputs?.quantityReceived ?? ''}
          label="Qty Received"
          onChange={event =>
            handleNumberChange(event.target.value, 'quantityReceived')
          }
          helperText={
            quantityError
              ? 'Quantity will cause Total Remaining to be negative'
              : baseQuantityText
          }
          error={quantityError}
          data-cy={`${dataCyPrefix}QuantityReceived`}
        />
        <div className={classes.costField}>
          <TextField
            slotProps={{
              inputLabel: { style: styles.label },
              input: {
                startAdornment: (
                  <span className={classes.inputAdornment}>$</span>
                ),
                endAdornment: (
                  <span
                    className={classes.inputAdornment}
                  >{`/${purchaseUnit.unit}`}</span>
                ),
              },
            }}
            type="number"
            value={inputs?.unitCost ?? ''}
            label="Unit Cost"
            onChange={event =>
              handleNumberChange(event.target.value, 'unitCost')
            }
            helperText={baseUnitCostText}
            data-cy={`${dataCyPrefix}UnitCost`}
          />
          {showUnitCostCalculate && renderCalculate('unitCost')}
        </div>
        <div className={classes.costField}>
          <TextField
            slotProps={{
              inputLabel: { style: styles.label },
              input: {
                startAdornment: (
                  <span className={classes.inputAdornment}>$</span>
                ),
              },
            }}
            type="number"
            value={inputs?.totalCost ?? ''}
            label="Total Cost"
            onChange={event =>
              handleNumberChange(event.target.value, 'totalCost')
            }
            data-cy={`${dataCyPrefix}TotalCost`}
          />
          {showTotalCostCalculate && renderCalculate('totalCost')}
        </div>
      </div>
      <div className={classes.inputContainer}>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          fullWidth
          value={inputs?.comments || ''}
          label="Comments (optional)"
          onChange={event => updateInput(event.target.value, 'comments')}
          data-cy={`${dataCyPrefix}Comments`}
        />
      </div>
      {subtextValue && (
        <div
          className={clsx(
            classes.remainingSubtext,
            isWarning && classes.warning,
          )}
          data-cy={`${dataCyPrefix}RemainingSubtext`}
        >
          <Icon
            color={iconColor}
            style={styles.icon}
            icon={subtextValue.icon}
          />
          <div>{subtextValue.message}</div>
        </div>
      )}
      {onConfirm && onCancel && (
        <div className={classes.createButtons}>
          <Button
            onClick={onCancel}
            style={styles.cancelButton}
            data-cy={`${dataCyPrefix}Cancel`}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={onConfirm}
            disabled={confirmDisabled}
            data-cy={`${dataCyPrefix}Confirm`}
          >
            {confirmText}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ADOTReplenishmentForm;
